/**
 * Combine the parent and child layer data.
 */

import OLSourceImageWMS from 'ol/source/ImageWMS'

import * as PoziUtils from 'api/utils'
import { featureCollectionIsSinglePoint } from 'utils/TurfUtils'

export default function GetChildCombinedURL (childLayer, currentTarget) {
  console.debug('GetChildCombinedURL', childLayer.titleid, childLayer, currentTarget)

  // Get a url with the full geometry
  let url = GetURL(childLayer, currentTarget)

  if (!url) {
    console.log('GetChildCombinedURL - No URL provided?', url)
    return url
  }

  // Use convenience function to replace variables
  url = PoziUtils.replaceUrlVariablesWithPropsValues(url, currentTarget.propertiesAll)

  // console.debug('GetChildCombinedURL - FNAL url', childLayer.titleid, url)

  return url
}

function GetURL (childLayer, currentTarget) {
  const { type } = childLayer || {}

  // console.debug('GetURL', childLayer.titleid, type)

  // Craft special URL for WMS
  if (type.match(/wms/i)) {
    return GetFeatureInfoUrl(childLayer, currentTarget)
  }

  const url = PoziUtils.getLayerUrl(childLayer)

  // console.debug('GetURL - url', childLayer.titleid, url)

  return url
}

function GetFeatureInfoUrl (childLayerConf, currentTarget) {
  const { titleid } = childLayerConf
  // console.debug('GetFeatureInfoUrl', titleid, childLayerConf, currentTarget)

  const selectionIsSinglePoint = featureCollectionIsSinglePoint(currentTarget.featureCollection)

  // console.debug('GetFeatureInfoUrl - selectionIsSinglePoint?', titleid, selectionIsSinglePoint)

  // Only use alternative GFI when target is a single point or (multi) linestring and server can handle it (i.e. is QGIS)
  // Also, we take into account that the geometry type could be 3D (i.e. PointZ, LineStringZ, MultiLineStringZM, MultiCurve, etc.)
  const layerIsQgis = childLayerConf.serverType === 'qgis'
  const geometryType = childLayerConf.geometryType || ''
  const layerGeometryTypeIsPoint = !!geometryType?.match(/point/i)
  const layerGeometryTypeIsLineString = !!geometryType?.match(/line|curve/i)

  // Only use the FILTER_GEOM when the server can handle it.
  // Do not use this for a single click on a point or line layer, as we have not implemented support for
  // a hit tolerance for WMS point/line features yet.
  const useQgisGeomFilter = layerIsQgis &&
    !(selectionIsSinglePoint && (layerGeometryTypeIsPoint || layerGeometryTypeIsLineString))

  // console.debug('GetFeatureInfoUrl - useQgisGeomFilter?', titleid, useQgisGeomFilter, selectionIsSinglePoint, layerGeometryTypeIsPoint, layerGeometryTypeIsLineString)
  // console.debug('GetFeatureInfoUrl - QGIS geometry type?', titleid, childLayerConf.geometryType)

  let url = ''
  if (useQgisGeomFilter) {
    url = getFeatureInfoUrlForQgisServer(childLayerConf, currentTarget)
  } else {
    url = getFeatureInfoUrlFromOlWmsSource(childLayerConf, currentTarget)
  }

  console.debug('GetFeatureInfoUrl - url', titleid, url)

  return url
}

const getFeatureInfoUrlForQgisServer = (childLayerConf, currentTarget) => {
  const { titleid } = childLayerConf

  console.debug('getFeatureInfoUrlForQgisServer', titleid, childLayerConf, currentTarget)

  const currentTargetFC = currentTarget.featureCollection

  const wmsParams = {
    INFO_FORMAT: 'application/json',
    FEATURE_COUNT: 10000000, // if not provided, results will be limited to 1
    WITH_GEOMETRY: 'TRUE',
    CRS: 'EPSG:4326',
    REQUEST: 'GetFeatureInfo',
    FILTER_GEOM: '[$wkt]', // this will be replaced with the WKT of the feature geometry when doing the request
    QUERY_LAYERS: childLayerConf.config?.params?.LAYERS,
    WIDTH: 1, // somehow the WIDTH and the HEIGHT parameters are required by QGIS
    HEIGHT: 1 // somehow the WIDTH and the HEIGHT parameters are required by QGIS
  }

  const url = PoziUtils.getLayerUrl(childLayerConf, wmsParams)

  // console.debug('getFeatureInfoUrlForQgisServer - url', titleid, url)

  return url
}

// Use the functionality built in the OL code to generate a GFI URL. We do this by creating an OLSourceImageWMS
// and have that generate the URL
const getFeatureInfoUrlFromOlWmsSource = (childLayerConf, currentTarget) => {
  console.debug('getFeatureInfoUrlFromOlWmsSource', childLayerConf.titleid, childLayerConf, currentTarget)

  const app = PoziUtils.getApp()

  // Use the location from the map marker. If that is not available, use the centroid of
  // the currentTarget.
  // Note: we should only do this if the target is a single point, single line or single polygon.
  //       For the polygon, we should calculate a point on surface, not the centroid!
  // TODO: improve this code to only create a URL if the above conditions are met.
  const positionFromMarker = app.selectMarker.getPosition()
  const currentTargetCentroid = app.transformFrom4326(currentTarget.featureCollectionCentroid?.geometry?.coordinates)
  const position = positionFromMarker || currentTargetCentroid

  // console.debug('getFeatureInfoUrlFromOlWmsSource - position from marker / centroid', childLayerConf.titleid, positionFromMarker, currentTargetCentroid)

  if (!position) {
    console.log('getFeatureInfoUrlFromOlWmsSource - no url due to no marker position', childLayerConf.titleid, app.selectMarker, currentTargetCentroid, currentTarget)

    return null
  }

  // Create options for WMS source from some default values and the layer config
  const wmsSourceOptions = {
    projection: 'EPSG:4326',
    crossOrigin: 'Anonymous',

    ...childLayerConf.config
  }

  // console.debug('getFeatureInfoUrlFromOlWmsSource - wmsSourceOptions', wmsSourceOptions)

  // Here we assume that the URL for the layer is in 'config.url' (and not 'config.spatial.url')
  const wmsSource = new OLSourceImageWMS(wmsSourceOptions)

  // Create params object for GFI request from default settings and the layer config
  const getFeatureInfoDefaultParams = {
    INFO_FORMAT: 'application/json',
    FEATURE_COUNT: 10000000, // if not provided, results will be limited to 1
    BUFFER: '5',
    FORMAT: 'image/png8',
    FI_POINT_TOLERANCE: '10',
    FI_LINE_TOLERANCE: '10',
    FI_POLYGON_TOLERANCE: '5',
    WITH_GEOMETRY: 'TRUE'
  }

  const childLayerParams = childLayerConf.config?.spatial?.params || childLayerConf.config?.params || {}

  const getFeatureInfoParams = {
    ...getFeatureInfoDefaultParams,
    ...childLayerParams
  }

  // console.debug('getFeatureInfoUrlFromOlWmsSource - getFeatureInfoParams', getFeatureInfoParams)

  const mapView = app.map.getView()
  const resolution = mapView.getResolution()
  const projection = mapView.getProjection()

  let url
  try {
    url = wmsSource.getFeatureInfoUrl(position, resolution, projection, getFeatureInfoParams)
  } catch (error) {
    console.log('getFeatureInfoUrlFromOlWmsSource - error', childLayerConf.titleid, url, error)
  }

  // console.debug('getFeatureInfoUrlFromOlWmsSource - url', childLayerConf.titleid, url)

  return url
}
